// Schema.org helpers for building JSON-LD blocks.

import { getStateByCode, DEFAULT_STATE } from './states';

const PROVIDER = {
  '@type': 'EducationalOrganization',
  '@id': 'https://steadyeducation.com/#organization',
  name: 'Steady Education',
  url: 'https://steadyeducation.com',
  sameAs: [
    'https://services.azre.gov/PdbWeb/School/ViewSchool/771'
  ],
};

// Build a BreadcrumbList schema from an ordered list of { name, url } items.
// The last item omits `item` — Google prefers the last crumb to be the current page (unlinked).
export function buildBreadcrumb(items) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((crumb, i) => {
      const el = {
        '@type': 'ListItem',
        position: i + 1,
        name: crumb.name,
      };
      // Include URL for all items EXCEPT the last (Google recommends this)
      if (i < items.length - 1 && crumb.url) {
        el.item = crumb.url;
      }
      return el;
    }),
  };
}

// Combine one or more page-specific schemas with breadcrumbs into a single @graph block.
// Pass the schemas WITHOUT '@context' — the wrapper provides it.
export function schemaGraph({ mainSchemas = [], breadcrumb = null } = {}) {
  const graph = [...mainSchemas];
  if (breadcrumb) graph.push(buildBreadcrumb(breadcrumb));
  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

// Convenience: guide-page schema with auto-built Home > Resources > <page> breadcrumb.
// Home › <State> › Resources › <Guide>. The state level was missing, and the Resources
// crumb pointed at /resources, which 301s to /arizona/resources — a breadcrumb should
// name the page it actually lands on, not a redirect.
export function guidePageSchema(mainSchema, { title, url, state = DEFAULT_STATE }) {
  const s = state || DEFAULT_STATE;
  return schemaGraph({
    mainSchemas: [mainSchema],
    breadcrumb: [
      { name: 'Home', url: 'https://steadyeducation.com' },
      { name: s.name, url: `https://steadyeducation.com/${s.slug}` },
      { name: 'Resources', url: `https://steadyeducation.com/${s.slug}/resources` },
      { name: title, url },
    ],
  });
}

// Course schema for individual course pages
export function courseSchema(course, url) {
  return {
    '@type': 'Course',
    name: course.title,
    description: course.short_description || course.description,
    url,
    provider: PROVIDER,
    educationalCredentialAwarded: `${course.credit_hours} CE Credit Hours (${(getStateByCode(course.state) || DEFAULT_STATE).name} Real Estate)`,
    ...(course.adre_course_number && { courseCode: course.adre_course_number }),
    offers: {
      '@type': 'Offer',
      price: String(parseFloat(course.price).toFixed(2)),
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url,
    },
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'online',
      courseWorkload: `PT${course.credit_hours || 3}H`,
    },
  };
}

// Course-list schema for a state's catalogue page. Google reads a list of courses as an
// ItemList whose items are Course objects, not as loose Course blocks — so the catalogue
// can qualify for course rich results while each course page keeps its own Course schema.
export function courseListSchema(courses, state) {
  const s = state || DEFAULT_STATE;
  return {
    '@type': 'ItemList',
    itemListElement: (courses || []).map((c, i) => {
      const url = `https://steadyeducation.com/courses/${c.id}`;
      const item = {
        '@type': 'Course',
        name: c.title,
        description: c.short_description || c.description
          || `${c.credit_hours}-hour ${s.regulator}-approved ${s.name} real estate continuing education course.`,
        url,
        provider: PROVIDER,
        educationalCredentialAwarded: `${c.credit_hours} CE Credit Hours (${s.name} Real Estate)`,
        offers: {
          '@type': 'Offer',
          price: String(parseFloat(c.price || 0).toFixed(2)),
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          url,
        },
        hasCourseInstance: {
          '@type': 'CourseInstance',
          courseMode: 'online',
          courseWorkload: `PT${parseFloat(c.credit_hours) || 3}H`,
        },
      };
      // The state regulator's course number (ADRE in AZ, KREC in KS) lives in the same column.
      if (c.adre_course_number) item.courseCode = c.adre_course_number;
      return { '@type': 'ListItem', position: i + 1, item };
    }),
  };
}

// Product schema for the $49.99 bundle on /courses.
// - `image` is required by Google's Merchant listings validator (was missing → GSC critical warning).
// - `brand` uses plain `Organization` (Merchant validator rejected the more-specific
//   `EducationalOrganization` subtype even though schema.org accepts it).
// - Digital-product fields deliberately omitted: shippingDetails, hasMerchantReturnPolicy
//   (these are for physical goods; Google keeps them as non-critical warnings only).
// Takes the state whose package is being offered, so a state page never advertises
// another state's package/price. Defaults to Arizona for callers that pass nothing.
export function bundleProductSchema(state = DEFAULT_STATE) {
  const s = state || DEFAULT_STATE;
  const isAZ = s.code === 'AZ';
  return {
    '@type': 'Product',
    name: `Complete ${s.name} Real Estate CE Package`,
    description: isAZ
      ? 'All 24 ADRE-required hours of Arizona real estate continuing education, including the 2025 Water, Firewise, and Deed Fraud requirements. Self-paced, online, with instant certificates.'
      : `All ${s.requiredHours} ${s.regulator}-required hours of ${s.name} real estate continuing education. Self-paced, online, with certificates.`,
    image: [
      'https://steadyeducation.com/android-chrome-512x512.png',
    ],
    brand: { '@type': 'Organization', name: 'Steady Education' },
    category: 'Continuing Education',
    offers: {
      '@type': 'Offer',
      price: String(s.packagePrice),
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: `https://steadyeducation.com/${s.slug}/courses`,
      seller: PROVIDER,
    },
  };
}
